import { AVATAR_COLORS, getFaculty, getProgram, type ItemSlot } from "@clubap/shared";
import { useEffect, useState } from "react";
import { fetchProfile, type PublicProfile } from "../api.js";
import { AVATAR_ART } from "../art/manifest.js";
import type { PlayerSelection } from "../game/RoomStage.js";
import { AvatarPreview } from "./AvatarPreview.js";
import { Wardrobe } from "./Wardrobe.js";
import { useDraggable } from "./useDraggable.js";
import { ClubBadges } from "./ClubBadges.js";
import { IconCarnet, IconLapiz, IconMochila, IconMoneda } from "./icons.js";

/** Cómo estás con quien tienes delante. Lo decide el panel social, no la tarjeta. */
export type CardRelation = "self" | "friend" | "request_sent" | "request_received" | "none";

interface Props {
  player: PlayerSelection;
  /** Retrato declarado en el manifest, o null. */
  portrait: string | null;
  isSelf: boolean;
  /** El que mira es admin y esta ficha no es la suya. */
  canManage: boolean;
  onManage: (profile: PublicProfile) => void;
  relation: CardRelation;
  /** Mandar solicitud. Recibe el id de CUENTA, que es lo estable. */
  onAddFriend: (accountId: string) => void;
  /** Abre mi propio perfil para editarlo. Sólo en mi ficha. */
  onEditProfile: () => void;
  /**
   * Tus créditos. Sólo en la ficha PROPIA: el saldo de otro no es asunto de nadie.
   *
   * Es el único número de la ficha que cambia mientras juegas, y por eso va en el
   * renglón de la carrera y no en el bloque de abajo: aquello describe la cuenta.
   *
   * Como todo lo demás de esta tarjeta, existe de verdad —sale del libro—. No hay ni un
   * dato decorativo acá: un "nivel" o un "promedio" se ven estupendos, y en cuanto el
   * lector descubre que uno era de adorno deja de creerse los otros.
   */
  coins?: number;
  /**
   * Ponerse y quitarse cosas, sólo en la ficha PROPIA.
   *
   * La ficha de otro enseña lo que lleva puesto y nada más: personalizar es de tu
   * cuenta, no una vista de alguien. Por eso llegan como opcionales en vez de vivir en
   * un panel aparte — la mochila era eso mismo con otra ventana delante.
   */
  onEquip?: (slot: ItemSlot, itemId: string | null) => void;
  onOpenShop?: () => void;
  /** Responder a quien me solicitó. La tarjeta no sabe el id de la solicitud. */
  onAccept: () => void;
  onReject: () => void;
  /** Retirar la solicitud que mandé yo. */
  onCancelRequest: () => void;
  /** Abrir la conversación con un amigo. */
  onMessage: () => void;
  onClose: () => void;
}

const RELATION_TEXT: Record<CardRelation, string> = {
  self: "Tú",
  friend: "Amigos",
  request_sent: "Enviada",
  request_received: "Te solicitó",
  none: "Estudiante",
};

/**
 * El carnet: el equivalente del "player card" de Club Penguin, en clave de ESPOL.
 *
 * Es un panel y no un modal a propósito: en un mundo social, tapar la sala para leer
 * un perfil te saca justo de lo que estabas haciendo.
 *
 * También es donde vive el arte detallado: el sprite del mundo mide 52px y no aguanta
 * detalle; acá se ve grande.
 *
 * Es además el sitio donde se ACTÚA sobre alguien. Antes sólo sabía agregar: si te
 * habían solicitado, la tarjeta te lo decía y no te dejaba responder, y para aceptar
 * había que ir a buscar la misma persona al panel social. La regla es que lo que se
 * puede hacer con quien tienes delante se pueda hacer donde lo tienes delante.
 */
export function PlayerCard({
  player,
  portrait,
  isSelf,
  canManage,
  onManage,
  relation,
  onAddFriend,
  onEditProfile,
  onAccept,
  onReject,
  onCancelRequest,
  onMessage,
  onClose,
  coins,
  onEquip,
  onOpenShop,
}: Props) {
  const [portraitFailed, setPortraitFailed] = useState(false);

  /*
   * La mochila, plegada de entrada.
   *
   * Abierta se convierte en una segunda columna a la derecha, no en más tarjeta hacia
   * abajo: al lado, cambias mirando la figura —que es contra lo que se compara al
   * ponerse algo— sin que nada se mueva de sitio.
   *
   * Es el mismo trato que la ficha de Club Penguin le da al suyo: un botón lo revela.
   */
  const [wardrobeOpen, setWardrobeOpen] = useState(false);

  /*
   * La ficha se mueve, igual que los paneles del editor y con el mismo código.
   *
   * Nace donde estaba antes anclada —bajo el carnet, a la izquierda— así que quien
   * no la arrastre nunca no nota el cambio. La posición se recuerda entre aperturas:
   * es una sola ficha aunque cambie de quién habla, y recolocarla en cada clic sería
   * pelearse con ella.
   */
  const drag = useDraggable<HTMLElement>({
    id: "ficha",
    width: 560,
    defaultPosition: () => ({ x: 12, y: 110 }),
  });
  const [profile, setProfile] = useState<PublicProfile | null>(null);

  // El retrato es por jugador: si cambias de perfil, vuelve a intentarse.
  useEffect(() => {
    setPortraitFailed(false);
  }, [player.sessionId]);

  /*
   * La ficha académica no viaja por el estado de la sala: se pide al abrir la
   * tarjeta. Cambia dos veces al año, y el estado es para lo que cambia por tick.
   */
  useEffect(() => {
    let cancelled = false;
    setProfile(null);

    void (async () => {
      try {
        const found = await fetchProfile(player.nickname);
        if (!cancelled) setProfile(found);
      } catch {
        // Sin ficha la tarjeta se ve como antes; no es un error que valga interrumpir.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [player.nickname]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const color = AVATAR_COLORS[player.color] ?? AVATAR_COLORS[0];
  const swatch = `#${color.toString(16).padStart(6, "0")}`;

  /*
   * Tres formas de enseñar a alguien, en orden de cuánto dicen.
   *
   * El retrato del manifest es un render a mano y gana si está. Si no, la figura
   * armada con las capas del avatar, que es la MISMA que ve el mundo, con su color y
   * lo que lleve puesto. La inicial es el último recurso: no dice nada de la persona,
   * sólo evita el hueco que parece un fallo de carga.
   */
  const showPortrait = portrait !== null && !portraitFailed;
  const showFigure = !showPortrait && (AVATAR_ART?.layers.length ?? 0) > 0;

  const faculty = getFaculty(profile?.faculty);
  const program = getProgram(profile?.faculty, profile?.program);

  /*
   * Sin `accountId` no hay a quién agregar.
   *
   * Pasa con una pestaña vieja conectada a un servidor anterior al campo: el estado
   * lo trae vacío, y un botón que siempre falla es peor que ninguno.
   */
  const known = player.accountId.length > 0;
  const canDress = isSelf && onEquip != null && onOpenShop != null;

  return (
    <aside
      ref={drag.ref}
      className={`panel card${canDress && wardrobeOpen ? " card--abierta" : ""}`}
      style={drag.style}
      aria-label={`Carnet de ${player.nickname}`}
      onPointerDown={drag.bringToFront}
    >
      {/* Se arrastra por la cabecera, no por el cuerpo: dentro hay botones y una
          rejilla que se pulsa, y agarrar ahí convertiría cada clic en un arrastre. */}
      <header className="sheet__head sheet__head--arrastrable" {...drag.handleProps}>
        <h2 className="sheet__title card__cinta">
          <IconCarnet />
          {isSelf ? "Tu carnet" : "Carnet"}
        </h2>
        <button className="icon-btn" type="button" onClick={onClose} aria-label="Cerrar carnet">
          ×
        </button>
      </header>

      <div className="card__split">
        <div className="card__perfil">
          {/*
            La figura, en su propio recuadro y a lo ancho de la tarjeta.

            El marco no es decoración: separa el render del fondo del panel, que es del
            mismo azul, y sin él la tortuga flotaba sobre nada. Es además lo que hace
            que un retrato a mano y la figura por capas —que tienen encuadres
            distintos— ocupen exactamente el mismo sitio.
          */}
          <div className="card__figura" style={showFigure ? undefined : { background: swatch }}>
            {showPortrait && (
              <img src={portrait} alt={player.nickname} onError={() => setPortraitFailed(true)} />
            )}
            {showFigure && <AvatarPreview worn={player.equipped} color={player.color} />}
            {!showPortrait && !showFigure && (
              // Sin retrato ni capas declaradas; docs/ART.md explica cómo agregarlos.
              <span className="card__initial">{player.nickname.slice(0, 1).toUpperCase()}</span>
            )}
          </div>

          <div className="card__identidad">
            <h3 className="card__nombre">{player.nickname}</h3>
            {/* La relación, en una pastilla al otro extremo: se busca ahí sin leerla. */}
            <span className="card__relacion">
              {isSelf ? RELATION_TEXT.self : RELATION_TEXT[relation]}
            </span>
          </div>

          {(faculty || profile?.semester != null || (isSelf && coins != null)) && (
            <p className="card__estudio">
              {faculty && <span className="card__faculty">{faculty.code}</span>}
              {faculty && <strong>{program ? program.name : faculty.name}</strong>}
              {faculty && profile?.semester != null && (
                <span className="card__punto" aria-hidden="true">
                  ·
                </span>
              )}
              {profile?.semester != null && <span>Semestre {profile.semester}</span>}
              {/*
                El saldo cierra el renglón, después de la carrera y el semestre.

                Tenía una ficha propia igual de grande que las otras dos, y no es un dato
                del mismo tipo: los otros dos describen la cuenta y éste es con cuánto
                cuentas ahora mismo. Acá se lee con lo que ya estabas leyendo y, de paso,
                el bloque de números baja de tres columnas a dos y respira.
              */}
              {isSelf && coins != null && (
                <span className="card__moneda">
                  <IconMoneda />
                  {coins}
                </span>
              )}
            </p>
          )}

          {/*
            Clubes y descripción, en la misma fila.

            Ninguno de los dos llena un renglón por su cuenta —una insignia y una línea
            de 140— así que apilados dejaban dos bandas medio vacías justo encima del
            pie. Juntos ocupan el ancho que ya estaba pagado y la tarjeta pierde alto,
            que en un carnet que reparte el suyo se lo devuelve a la figura.

            Y si sólo hay uno, se lleva la fila entera: nada queda a medio ancho por
            respetar un hueco que no tiene nada dentro.
          */}
          {(player.clubs.length > 0 || profile?.bio) && (
            <div className="card__abajo">
              {player.clubs.length > 0 && (
                <div className="card__seccion">
                  <h4 className="card__rotulo">Clubes</h4>
                  <div className="card__clubs">
                    <ClubBadges clubs={player.clubs} withNames />
                  </div>
                </div>
              )}

              {profile?.bio && <p className="card__bio">{profile.bio}</p>}
            </div>
          )}
        </div>

        {/*
          Sólo en la tuya: la ficha de otro enseña su aspecto y no se toca. Personalizar
          es de tu cuenta, no una vista de alguien.
        */}
        {canDress && wardrobeOpen && (
          <div className="card__mochila">
            <header className="card__mochilaCabecera">
              <h3 className="card__mochilaTitulo">Mochila</h3>
              <button
                className="icon-btn"
                type="button"
                onClick={() => setWardrobeOpen(false)}
                aria-label="Cerrar mochila"
              >
                ×
              </button>
            </header>

            <Wardrobe equipped={player.equipped} onEquip={onEquip!} onOpenShop={onOpenShop!} />
          </div>
        )}
      </div>

      {/*
          Todo lo que se puede hacer, en una banda al pie.

          Antes "Agregar amigo" salía en medio de los datos y "Administrar cuenta" al
          final del todo, así que las acciones no formaban un grupo y aparecían y
          desaparecían moviendo el texto de sitio. Juntas abajo, la tarjeta cambia de
          alto pero nunca de forma.
        */}
      <div className="card__acciones">
        {canDress && (
          <button
            className={`btn card__accion${wardrobeOpen ? " btn--on" : ""}`}
            type="button"
            onClick={() => setWardrobeOpen((open) => !open)}
            aria-expanded={wardrobeOpen}
          >
            <IconMochila size={18} />
            {wardrobeOpen ? "Cerrar" : "Mochila"}
          </button>
        )}

        {isSelf && (
          <button className="btn btn--ghost card__accion" type="button" onClick={onEditProfile}>
            <IconLapiz />
            Editar perfil
          </button>
        )}

        {!isSelf && known && relation === "none" && (
          <button
            className="btn card__accion"
            type="button"
            onClick={() => onAddFriend(player.accountId)}
          >
            Agregar amigo
          </button>
        )}

        {!isSelf && known && relation === "request_received" && (
          <>
            <button className="btn card__accion" type="button" onClick={onAccept}>
              Aceptar
            </button>
            <button className="btn btn--ghost card__accion" type="button" onClick={onReject}>
              Rechazar
            </button>
          </>
        )}

        {!isSelf && known && relation === "request_sent" && (
          <button className="btn btn--ghost card__accion" type="button" onClick={onCancelRequest}>
            Cancelar solicitud
          </button>
        )}

        {!isSelf && known && relation === "friend" && (
          <button className="btn card__accion" type="button" onClick={onMessage}>
            Enviar mensaje
          </button>
        )}

        {canManage && profile && (
          <button
            className="btn btn--ghost card__accion"
            type="button"
            onClick={() => onManage(profile)}
          >
            Administrar
          </button>
        )}
      </div>
    </aside>
  );
}
